<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';

import Button from 'dashboard/components-next/button/Button.vue';
import Breadcrumb from 'dashboard/components-next/breadcrumb/Breadcrumb.vue';

const props = defineProps({
  selectedContact: {
    type: Object,
    default: () => ({}),
  },
  isUpdating: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['goToContactsList', 'toggleBlock']);

const { t } = useI18n();

const isMenuOpen = ref(false);

const selectedContactName = computed(() => {
  return props.selectedContact?.name;
});

const breadcrumbItems = computed(() => {
  const items = [
    {
      label: t('CONTACTS_LAYOUT.HEADER.BREADCRUMB.CONTACTS'),
      link: '#',
    },
  ];
  if (props.selectedContact) {
    items.push({
      label: selectedContactName.value,
    });
  }
  return items;
});

const isContactBlocked = computed(() => {
  return props.selectedContact?.blocked;
});

const truthy = value =>
  value === true || value === 'true' || value === 1 || value === '1';

const isTrainingContact = computed(() => {
  const customAttributes = props.selectedContact?.customAttributes || {};
  const customAttributesSnake = props.selectedContact?.custom_attributes || {};
  const additionalAttributes =
    props.selectedContact?.additionalAttributes || {};
  const additionalAttributesSnake =
    props.selectedContact?.additional_attributes || {};

  return (
    truthy(customAttributes.daily_training) ||
    truthy(customAttributesSnake.daily_training) ||
    truthy(additionalAttributes.daily_training) ||
    truthy(additionalAttributesSnake.daily_training) ||
    Boolean(customAttributes.daily_training_session_id) ||
    Boolean(customAttributesSnake.daily_training_session_id) ||
    Boolean(additionalAttributes.daily_training_session_id) ||
    Boolean(additionalAttributesSnake.daily_training_session_id) ||
    Boolean(customAttributes.custom_training_id) ||
    Boolean(customAttributesSnake.custom_training_id) ||
    Boolean(additionalAttributes.custom_training_id) ||
    Boolean(additionalAttributesSnake.custom_training_id)
  );
});

const handleBreadcrumbClick = () => {
  emit('goToContactsList');
};

const toggleBlock = () => {
  emit('toggleBlock', isContactBlocked.value);
};
</script>

<template>
  <section
    class="contact-detail-page h-full w-full overflow-y-auto bg-[#f5f5f3] p-4 text-[#202939] md:p-8 [scrollbar-width:thin] max-md:overflow-x-hidden"
  >
    <div class="mx-auto w-full max-w-[1440px]">
      <Breadcrumb
        :items="breadcrumbItems"
        class="mb-4 text-xs"
        @click="handleBreadcrumbClick"
      />
      <header
        class="contact-detail-page__header grid grid-cols-[minmax(0,1fr)_40px] items-center gap-4 rounded-[20px] bg-white p-5 shadow-[0_12px_32px_rgba(0,0,0,0.05)] max-md:grid-cols-[72px_minmax(0,1fr)_40px] max-md:gap-x-3 max-md:gap-y-3 max-md:p-4"
      >
        <div
          id="contact-detail-profile"
          class="min-w-0 flex-1 max-md:contents"
        />
        <div class="flex shrink-0 items-center gap-2 max-md:col-start-3 max-md:row-start-3 max-md:justify-end">
          <div
            v-on-click-outside="() => (isMenuOpen = false)"
            class="relative"
            @keydown.esc="isMenuOpen = false"
          >
            <Button
              icon="i-ph-dots-three-vertical"
              size="sm"
              slate
              ghost
              class="!size-10 !rounded-[10px] !border !border-solid !border-n-weak"
              :aria-label="t('CONTACT_DETAIL_VIEW.MORE')"
              :aria-expanded="isMenuOpen"
              @click="isMenuOpen = !isMenuOpen"
            />
            <div
              v-if="isMenuOpen"
              class="absolute right-0 top-full z-20 mt-2 min-w-48 rounded-[14px] border border-n-weak bg-white p-1.5 shadow-lg"
            >
              <Button
                v-if="!isTrainingContact"
                :label="
                  !isContactBlocked
                    ? $t('CONTACTS_LAYOUT.HEADER.BLOCK_CONTACT')
                    : $t('CONTACTS_LAYOUT.HEADER.UNBLOCK_CONTACT')
                "
                :icon="
                  isContactBlocked ? 'i-lucide-shield-check' : 'i-lucide-ban'
                "
                justify="start"
                size="sm"
                slate
                ghost
                class="w-full"
                :is-loading="isUpdating"
                :disabled="isUpdating"
                @click="
                  toggleBlock();
                  isMenuOpen = false;
                "
              />
              <slot name="actions" />
            </div>
          </div>
        </div>
      </header>
      <nav
        class="contact-detail-page__tabs mt-4 mb-4 flex gap-6 overflow-x-auto border-b border-[#e9ebee] max-md:grid max-md:grid-cols-4 max-md:gap-0 max-md:overflow-x-hidden"
        :aria-label="t('CONTACT_DETAIL_VIEW.MORE')"
      >
        <slot name="tabs" />
      </nav>
      <main><slot /></main>
    </div>
  </section>
</template>
